import { api } from '@/lib/api/requests'
import { isApiError } from '@/lib/api/errors'
import { rememberPublicScheduleVisibility } from '@/features/clubs/publicScheduleVisibility'
import { rememberLinkLane } from '@/features/clubs/linkLane'
import { rememberStorefront } from '@/features/clubs/storefront'
import { rememberClubCapabilities } from '@/features/organization/clubCapabilitiesStore'
import type {
  PublicClubRow,
  PublicClubDetails,
  PublicClubReviewsResponse,
  PublicClubsResponse,
} from '@/features/clubs/types'
import { rememberClubTax } from '@/features/payments/clubTaxRate'

export type FetchClubBySlugResult =
  | { ok: true; details: PublicClubDetails }
  | { ok: false; redirectSlug: string }
  | { ok: false; status: 404 }
  | { ok: false; status: number }

function normalizeCoordinate(value: unknown, min: number, max: number): number | null {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  if (numeric < min || numeric > max) return null
  return Math.round(numeric * 1_000_000) / 1_000_000
}

function normalizePublicClubRow(row: PublicClubRow | null | undefined): PublicClubRow | null {
  if (!row || typeof row !== 'object') return null
  // Every public-club read passes through here, so this is the one place that can teach
  // the rest of the app whether a club publishes its calendar — which is why the
  // organization page's gate usually costs nothing (publicScheduleVisibility.ts).
  rememberPublicScheduleVisibility(row)
  // ...and whether it runs its own storefront, so the chrome — which sits above the
  // route and only ever sees a slug — can answer without a read of its own. Learning it
  // from the DIRECTORY read too is what makes the club's own chrome already correct for
  // a visitor who arrives by clicking a card (storefront.ts).
  rememberStorefront(row)
  // …and whether it takes payment through its own link — where "I've paid" may exist (A3).
  rememberLinkLane(row)
  // …and its sales tax rate (D3.16): a read with the payment facts says off by leaving it out.
  rememberClubTax(row, typeof row.takesLinkPayments === 'boolean')
  // …and what it chose to run (D16), when the row says — the signed-out board has no other read.
  rememberClubCapabilities(row, false)
  const legacy = row as Record<string, unknown>
  const latitude = normalizeCoordinate(legacy.latitude ?? legacy.lat, -90, 90)
  const longitude = normalizeCoordinate(legacy.longitude ?? legacy.lng ?? legacy.lon, -180, 180)
  return {
    ...row,
    latitude,
    longitude,
  }
}

function normalizeFilterValues(values: string[] | null | undefined, allowed: string[]): string[] {
  const allowedSet = new Set(allowed)
  const dedupe = new Set<string>()
  const source = Array.isArray(values) ? values : []
  source.forEach((value) => {
    const normalized = String(value || '').trim().toLowerCase()
    if (!allowedSet.has(normalized) || dedupe.has(normalized)) return
    dedupe.add(normalized)
  })
  return Array.from(dedupe)
}

export async function fetchPublicClubs(filters: {
  sport?: string[]
  surface?: string[]
  environment?: string[]
} = {}): Promise<PublicClubsResponse> {
  const params = new URLSearchParams()
  const sports = normalizeFilterValues(filters.sport, ['tennis', 'padel', 'pickle'])
  const surfaces = normalizeFilterValues(filters.surface, ['hard', 'clay', 'grass', 'artificial', 'carpet'])
  const environments = normalizeFilterValues(filters.environment, ['indoor', 'outdoor'])
  if (sports.length) params.set('sport', sports.join(','))
  if (surfaces.length) params.set('surface', surfaces.join(','))
  if (environments.length) params.set('environment', environments.join(','))
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await api.get<PublicClubsResponse>(`/clubs/public${suffix}`, { handleAuthErrors: false })
  const clubs = Array.isArray(response?.clubs) ? response.clubs.map((row) => normalizePublicClubRow(row)).filter(Boolean) : []
  return {
    ...response,
    clubs: clubs as PublicClubRow[],
  }
}

function normalizePublicClubDetails(response: PublicClubDetails): PublicClubDetails {
  return {
    ...response,
    club: normalizePublicClubRow(response?.club) || undefined,
  }
}

/**
 * Resolves a club slug AND its full details in one round trip (perf
 * F4): ?include=details returns the same { club } payload as
 * GET /clubs/public/:orgId. Auth rides along (not omitted) because
 * joined/role in the payload are viewer-specific. Renamed slugs still
 * surface as 301 { redirectSlug } and missing clubs as 404 — those
 * responses are thin and unaffected by the flag.
 */
export async function fetchClubBySlug(slug: string): Promise<FetchClubBySlugResult> {
  try {
    const response = await api.get<PublicClubDetails>(
      `/clubs/public/by-slug/${encodeURIComponent(slug)}?include=details`,
      { handleAuthErrors: false },
    )
    return { ok: true, details: normalizePublicClubDetails(response) }
  } catch (error) {
    if (isApiError(error)) {
      if (error.status === 301) {
        const payload = error.payload as { redirectSlug?: string } | undefined
        const redirectSlug = String(payload?.redirectSlug || '').trim()
        if (redirectSlug) return { ok: false, redirectSlug }
      }
      if (error.status === 404) return { ok: false, status: 404 }
      return { ok: false, status: error.status }
    }
    throw error
  }
}

export async function fetchPublicClubDetails(orgId: string): Promise<PublicClubDetails> {
  const response = await api.get<PublicClubDetails>(`/clubs/public/${encodeURIComponent(orgId)}`, {
    handleAuthErrors: false,
  })
  return normalizePublicClubDetails(response)
}

export async function fetchPublicClubReviews(
  orgId: string,
  options: { limit?: number; cursor?: string } = {},
): Promise<PublicClubReviewsResponse> {
  const params = new URLSearchParams()
  if (Number.isFinite(options.limit)) params.set('limit', String(options.limit))
  if (String(options.cursor || '').trim()) params.set('cursor', String(options.cursor))
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return api.get<PublicClubReviewsResponse>(`/organizations/${encodeURIComponent(orgId)}/reviews${suffix}`, {
    handleAuthErrors: false,
  })
}

export async function createPublicClubReview(
  orgId: string,
  payload: { rating: number; text?: string },
): Promise<PublicClubReviewsResponse & { review?: unknown }> {
  return api.post<PublicClubReviewsResponse & { review?: unknown }>(
    `/organizations/${encodeURIComponent(orgId)}/reviews`,
    {
      body: payload,
      handleAuthErrors: false,
    },
  )
}

export async function updatePublicClubReview(
  orgId: string,
  reviewId: string,
  payload: { rating?: number; text?: string },
): Promise<PublicClubReviewsResponse & { review?: unknown }> {
  return api.patch<PublicClubReviewsResponse & { review?: unknown }>(
    `/organizations/${encodeURIComponent(orgId)}/reviews/${encodeURIComponent(reviewId)}`,
    {
      body: payload,
      handleAuthErrors: false,
    },
  )
}

export async function deletePublicClubReview(
  orgId: string,
  reviewId: string,
): Promise<PublicClubReviewsResponse & { deleted?: boolean }> {
  return api.del<PublicClubReviewsResponse & { deleted?: boolean }>(
    `/organizations/${encodeURIComponent(orgId)}/reviews/${encodeURIComponent(reviewId)}`,
    {
      handleAuthErrors: false,
    },
  )
}

export async function joinClub(orgId: string): Promise<Record<string, unknown>> {
  return api.post<Record<string, unknown>>(`/organizations/${encodeURIComponent(orgId)}/join`, {
    handleAuthErrors: false,
  })
}

export async function leaveClub(orgId: string): Promise<Record<string, unknown>> {
  return api.del<Record<string, unknown>>(`/organizations/${encodeURIComponent(orgId)}/join`, {
    handleAuthErrors: false,
  })
}
